import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import AdminPortalLayout from './AdminPortalLayout';
import { AdminMe, AppSettings, fetchAppSettings, updateAppSettings } from '../lib/admin-portal-api';
import { FaCheckCircle, FaExclamationTriangle } from '../lib/icons';

interface Props { me: AdminMe }

export default function AdminPortalSettings({ me }: Props) {
  const [draft, setDraft] = useState<AppSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchAppSettings()
      .then((res) => { setDraft(res.data || {}); })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load settings' }))
      .finally(() => setLoading(false));
  }, []);

  if (!me.isSuperAdmin) return <Navigate to="/admin-portal" replace />;

  const set = <K extends keyof AppSettings>(k: K, v: AppSettings[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload: Partial<AppSettings> = { ...draft };
      delete (payload as any).id;
      await updateAppSettings(payload);
      setMsg({ type: 'success', text: 'Settings saved.' });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error || 'Save failed' });
    } finally { setSaving(false); }
  };

  return (
    <AdminPortalLayout me={me} active="settings">
      <Helmet><title>Settings — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Application Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Marketing copy, app links, and community policy toggles.</p>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-start gap-2 mb-4 ${
          msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                                 : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-4">
          {/* Policy toggles */}
          <Section title="Community policy">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox"
                checked={draft.autoApproveCommunityPosts === true}
                onChange={(e) => set('autoApproveCommunityPosts', e.target.checked)}
                className="mt-1 accent-primary" />
              <div>
                <div className="text-sm font-semibold text-gray-800">Auto-approve community posts</div>
                <div className="text-xs text-gray-500">When on, new posts skip the pending queue. Moderator and admin posts auto-approve either way.</div>
              </div>
            </label>
          </Section>

          {/* App store links */}
          <Section title="App store links">
            <Field label="iOS App Store URL"
              value={draft.iosAppLink} onChange={(v) => set('iosAppLink', v)} />
            <Field label="Google Play URL"
              value={draft.androidAppLink} onChange={(v) => set('androidAppLink', v)} />
          </Section>

          {/* Hero & CTA copy */}
          <Section title="Marketing copy">
            <Field label="Hero title" textarea value={draft.heroTitle} onChange={(v) => set('heroTitle', v)} />
            <Field label="Hero subtitle" textarea value={draft.heroSubtitle} onChange={(v) => set('heroSubtitle', v)} />
            <Field label="CTA title" value={draft.ctaTitle} onChange={(v) => set('ctaTitle', v)} />
            <Field label="CTA subtitle" textarea value={draft.ctaSubtitle} onChange={(v) => set('ctaSubtitle', v)} />
            <Field label="Promo banner" value={draft.promoText} onChange={(v) => set('promoText', v)} />
          </Section>

          {/* Diet content */}
          <Section title="Diet labels">
            <Field label="Dietary choice" value={draft.DietaryChoice} onChange={(v) => set('DietaryChoice', v)} />
            <Field label="Carnivore details" textarea value={draft.CarnivoreDetails} onChange={(v) => set('CarnivoreDetails', v)} />
            <Field label="Ketovore details" textarea value={draft.KetovoreDetails} onChange={(v) => set('KetovoreDetails', v)} />
            <Field label='"Choose multiple options" text' value={draft.ChooseMultipleOptionsText} onChange={(v) => set('ChooseMultipleOptionsText', v)} />
            <Field label="Feature: Food choices" value={draft.FeatureFoodChoices} onChange={(v) => set('FeatureFoodChoices', v)} />
            <Field label="Feature: Health & diet tracker" value={draft.FeatureHealthAndDietTracker} onChange={(v) => set('FeatureHealthAndDietTracker', v)} />
          </Section>

          {/* Community copy */}
          <Section title="Community page copy">
            <Field label="Header (Carnivore)" value={draft.communityHeaderCarnivore} onChange={(v) => set('communityHeaderCarnivore', v)} />
            <Field label="Subheader (Carnivore)" textarea value={draft.communitySubheaderCarnivore} onChange={(v) => set('communitySubheaderCarnivore', v)} />
            <Field label="Header (Ketovore)" value={draft.communityHeaderKetovore} onChange={(v) => set('communityHeaderKetovore', v)} />
            <Field label="Subheader (Ketovore)" textarea value={draft.communitySubheaderKetovore} onChange={(v) => set('communitySubheaderKetovore', v)} />
          </Section>

          <div className="sticky bottom-4 flex justify-end">
            <button onClick={save} disabled={saving}
              className="px-6 py-3 rounded-xl bg-primary text-white text-sm font-semibold shadow-lg hover:bg-primary/90 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </AdminPortalLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <h3 className="font-bold text-gray-800 mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, textarea = false }: {
  label: string; value: string | null | undefined; onChange: (v: string) => void; textarea?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {textarea ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none resize-y" />
      ) : (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" />
      )}
    </div>
  );
}
